import React, { useState, useRef, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './InteractiveMapModal.css';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface InteractiveMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCoordinates: [number, number];
  hospitalName: string;
  onCoordinatesChange: (coordinates: [number, number]) => void;
}

const DraggableMarker: React.FC<{
  position: [number, number];
  onPositionChange: (position: [number, number]) => void;
}> = ({ position, onPositionChange }) => {
  const [draggable] = useState(true);
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const newPos = marker.getLatLng();
          onPositionChange([newPos.lng, newPos.lat]);
        }
      },
    }),
    [onPositionChange],
  );

  return (
    <Marker
      draggable={draggable}
      eventHandlers={eventHandlers}
      position={[position[1], position[0]]}
      ref={markerRef}
    />
  );
};

const MapClickHandler: React.FC<{
  onMapClick: (coordinates: [number, number]) => void;
}> = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick([e.latlng.lng, e.latlng.lat]);
    },
  });
  return null;
};

const InteractiveMapModal: React.FC<InteractiveMapModalProps> = ({
  isOpen,
  onClose,
  initialCoordinates,
  hospitalName,
  onCoordinatesChange,
}) => {
  const [position, setPosition] = useState<[number, number]>(initialCoordinates);
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef<L.Map>(null);

  // Update position when modal opens with new coordinates
  useEffect(() => {
    if (isOpen) {
      setPosition(initialCoordinates);
      // Delay map rendering to ensure modal is fully visible
      setTimeout(() => setShowMap(true), 200);
    } else {
      setShowMap(false);
    }
  }, [isOpen, initialCoordinates]);

  // Invalidate map size when it becomes visible
  useEffect(() => {
    if (showMap && mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 100);
    }
  }, [showMap]);

  if (!isOpen) return null;

  const handlePositionChange = (newPosition: [number, number]) => {
    setPosition(newPosition);
    onCoordinatesChange(newPosition);
  };

  const handleMapClick = (coordinates: [number, number]) => {
    setPosition(coordinates);
    onCoordinatesChange(coordinates);
  };

  return (
    <div className="interactive-map-overlay" onClick={onClose}>
      <div className="interactive-map-content" onClick={(e) => e.stopPropagation()}>
        <div className="interactive-map-header">
          <h3>{hospitalName}</h3>
          <button className="interactive-map-close" onClick={onClose}>×</button>
        </div>
        <div className="interactive-map-container">
          {showMap && position && position[0] !== 0 && position[1] !== 0 ? (
            <MapContainer
              center={[position[1], position[0]]}
              zoom={15}
              style={{ height: '400px', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <DraggableMarker
                position={position}
                onPositionChange={handlePositionChange}
              />
              <MapClickHandler onMapClick={handleMapClick} />
            </MapContainer>
          ) : (
            <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
              Loading map...
            </div>
          )}
        </div>
        <div className="interactive-map-footer">
          <p>Coordinates: Lng: {position[0].toFixed(7)}, Lat: {position[1].toFixed(7)}</p>
          <p className="map-instructions">Drag the marker or click on the map to change location</p>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMapModal;